import AlertCircle from "@uui/AlertCircle";
import CheckCircle from "@uui/CheckCircle";

import {
  Chip,
  Skeleton,
  SkeletonProps,
  Typography,
  TypographyProps,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

import { SemanticScore } from "@app/common/utils";

import Stack, { Row, StackProps } from "@rui/Stack";

export const ContentContainer = styled(Row)({
  height: "calc(100vh - 60px)", // 60px = height of action bar
  overflow: "hidden",
});

export const LeftContainer = styled(Stack)(({ theme }) => ({
  width: "475px",
  backgroundColor: "#FFF",
  height: "100%",
  borderRight: `2px solid ${theme.palette.shades.neutral[100]}`,
  padding: theme.spacing(3),
}));

export const RightOuterContainer = styled(Stack)(({ theme }) => ({
  padding: "24px",
  [theme.breakpoints.up("lg")]: {
    padding: "24px 40px",
  },
  flex: 1,
  height: "100%",
  overflow: "auto",
  backgroundColor: theme.palette.shades.ghost[300],
}));

export const RightInnerContainer = styled(Stack)(({ theme }) => ({
  maxWidth: "100%",
  paddingBottom: theme.spacing(2),
  minWidth: "475px",
  flex: 1,
}));

export const Description = styled((props: TypographyProps) => (
  <Typography variant="labelReg" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[500],
}));

type ChipProps = { variant: SemanticScore };

export const FeatureScoreChip = styled(({ variant, ...props }: ChipProps) => (
  <Chip label={variant === "positive" ? "Good" : "Needs Work"} {...props} />
))(({ theme, variant }) => ({
  borderRadius: "4px",
  padding: "2px 4px",
  height: "auto",
  minWidth: 0,
  backgroundColor:
    variant === "positive"
      ? theme.palette.shades.meadow[50]
      : theme.palette.shades.orange[50],
  color:
    variant === "positive"
      ? theme.palette.shades.meadow[600]
      : theme.palette.shades.orange[600],
  textTransform: "uppercase",
  "& .MuiChip-label": {
    fontSize: "0.75rem",
    padding: 0,
    fontWeight: 500,
  },
}));

export const FeatureHeading = styled((props: TypographyProps) => (
  <Typography variant="h3" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[800],
}));

export const FeatureHeader = styled((props: StackProps) => (
  <Row variant="left" spacing={1} {...props} />
))(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

type FeatureItemProps = StackProps & { selected?: boolean };

export const FeatureItem = styled(
  ({ selected: _, ...props }: FeatureItemProps) => (
    <Row {...props} variant="apart" />
  )
)(({ selected, theme }) => ({
  marginTop: theme.spacing(1),
  padding: "12px 16px",
  borderRadius: "8px",
  border: "2px solid",
  borderColor: selected
    ? theme.palette.shades.primary[500]
    : theme.palette.shades.neutral[100],
  "&:hover": {
    cursor: "pointer",
    borderColor: selected
      ? theme.palette.shades.primary[500]
      : theme.palette.shades.ghost[700],
  },
}));

export const SectionHeading = styled((props: TypographyProps) => (
  <Typography variant="h2" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[800],
}));

export const SectionDescription = styled((props: TypographyProps) => (
  <Typography variant="bodySecondaryReg" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[500],
}));

export const SectionSubHeading = styled((props: TypographyProps) => (
  <Typography variant="h3" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[800],
}));

export const SectionOverviewContainer = styled(Stack)(({ theme }) => ({
  flexDirection: "column",
  paddingTop: theme.spacing(1),
  [theme.breakpoints.up("lg")]: {
    flexDirection: "row",
  },
}));

type CardProps = StackProps & { title: string; subtitle?: string };

const CardTitle = styled((props: TypographyProps) => (
  <Typography variant="bodyPrimaryMed" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[700],
}));

const CardSubtitle = styled((props: TypographyProps) => (
  <Typography variant="labelReg" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[500],
}));

export const Card = styled(
  ({ children, title, subtitle, ...props }: CardProps) => (
    <Stack {...props} spacing={2}>
      <Row variant="apart" spacing={1}>
        <CardTitle>{title}</CardTitle>
        {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
      </Row>
      {children}
    </Stack>
  )
)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("lg")]: {
    width: "calc(50% - 8px)",
  },
  padding: theme.spacing(2),
  backgroundColor: "#FFF",
  borderRadius: "8px",
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
}));

export const CardText = styled((props: TypographyProps) => (
  <Typography variant="bodySecondaryReg" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[600],
}));

export const EmptyCardText = styled((props: TypographyProps) => (
  <Typography variant="bodySecondaryReg" {...props}>
    Nothing to report on, yet
  </Typography>
))(({ theme }) => ({
  color: theme.palette.shades.neutral[500],
}));

type ValueWithSemanticIconProps = TypographyProps & { score: SemanticScore };

export const ValueWithSemanticIcon = styled(
  ({ score, ...props }: ValueWithSemanticIconProps) => (
    <Row spacing={1} variant="left" sx={{ pr: 1 }}>
      <Typography {...props} />
      {score === "unknown" ? null : score === "positive" ? (
        <CheckCircle sx={{ fontSize: "16px", color: "shades.meadow.500" }} />
      ) : (
        <AlertCircle sx={{ fontSize: "16px", color: "shades.orange.500" }} />
      )}
    </Row>
  )
)(({ theme }) => ({ color: theme.palette.shades.neutral[800] }));

export const LoadingSkeleton = styled((props: SkeletonProps) => (
  <Skeleton variant="rectangular" {...props} />
))({
  borderRadius: "8px",
});
